/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowLeft, Plus, Trash2, Save, X, Pencil } from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  useCareerEntries,
  useCreateCareerEntry,
  useDeleteCareerEntry,
  useUpdateCareerEntry,
} from "@/hooks/api/useCareerEntries";
import { useClubs } from "@/hooks/api/useClubs";
import type { CareerEntry } from "@/api/services/career-entry.service";

export default function PlayerCareerPage() {
  const { id } = useParams<{ id: string }>();
  const playerId = Number(id);
  const navigate = useNavigate();

  const { data, isLoading, error } = useCareerEntries(playerId || null);
  const { data: clubsData, isLoading: clubsLoading } = useClubs();
  const createMutation = useCreateCareerEntry(playerId);
  const updateMutation = useUpdateCareerEntry(playerId);
  const deleteMutation = useDeleteCareerEntry(playerId);

  const [clubId, setClubId] = useState<string>("");
  const [fromYear, setFromYear] = useState<string>("");
  const [toYear, setToYear] = useState<string>("");

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editClubId, setEditClubId] = useState<string>("");
  const [editFrom, setEditFrom] = useState<string>("");
  const [editTo, setEditTo] = useState<string>("");

  const clubs = clubsData?.clubs ?? [];
  const entries = useMemo(
    () => [...(data?.entries ?? [])].sort((a, b) => a.order - b.order),
    [data?.entries],
  );

  const handleAdd = () => {
    if (!clubId) {
      toast.error("Pick a club");
      return;
    }
    const from = fromYear ? Number(fromYear) : undefined;
    const to = toYear ? Number(toYear) : undefined;
    if (from && to && from > to) {
      toast.error("fromYear must be ≤ toYear");
      return;
    }

    createMutation.mutate(
      { clubId: Number(clubId), fromYear: from, toYear: to },
      {
        onSuccess: () => {
          toast.success("Career entry added");
          setClubId("");
          setFromYear("");
          setToYear("");
        },
        onError: (e: any) =>
          toast.error(e?.response?.data?.message || "Failed to add entry"),
      },
    );
  };

  const startEdit = (entry: CareerEntry) => {
    setEditingId(entry.id);
    setEditClubId(String(entry.clubId));
    setEditFrom(entry.fromYear?.toString() ?? "");
    setEditTo(entry.toYear?.toString() ?? "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditClubId("");
    setEditFrom("");
    setEditTo("");
  };

  const saveEdit = (entryId: number) => {
    const from = editFrom === "" ? null : Number(editFrom);
    const to = editTo === "" ? null : Number(editTo);
    if (from !== null && to !== null && from > to) {
      toast.error("fromYear must be ≤ toYear");
      return;
    }
    updateMutation.mutate(
      {
        entryId,
        data: {
          clubId: Number(editClubId),
          fromYear: from,
          toYear: to,
        },
      },
      {
        onSuccess: () => {
          toast.success("Entry updated");
          cancelEdit();
        },
        onError: (e: any) =>
          toast.error(e?.response?.data?.message || "Failed to update entry"),
      },
    );
  };

  const handleDelete = (entryId: number) => {
    if (!confirm("Delete this career entry?")) return;
    deleteMutation.mutate(entryId, {
      onSuccess: () => toast.success("Entry deleted"),
      onError: (e: any) =>
        toast.error(e?.response?.data?.message || "Failed to delete"),
    });
  };

  if (error) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64">
          <p className="text-red-600 mb-4">Failed to load career entries</p>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mb-6 flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Career Timeline
            {data?.player ? (
              <span className="text-gray-500 font-medium"> — {data.player.name}</span>
            ) : null}
          </h1>
          <p className="text-gray-500 mt-1 text-sm">
            Used by Football Grid validation. Leave <span className="font-semibold">toYear</span> empty for the player's current club.
          </p>
        </div>
      </div>

      {/* Add-new-entry form */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <h2 className="text-lg font-semibold mb-3">Add Club</h2>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[220px] flex-1">
            <label className="text-xs text-gray-500 block mb-1">Club</label>
            <Select value={clubId} onValueChange={setClubId}>
              <SelectTrigger>
                <SelectValue
                  placeholder={clubsLoading ? "Loading clubs..." : "Select a club"}
                />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                {clubs.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    {c.name}
                    {c.league ? (
                      <span className="text-gray-400 text-xs ml-2">
                        ({c.league})
                      </span>
                    ) : null}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-28">
            <label className="text-xs text-gray-500 block mb-1">From year</label>
            <Input
              type="number"
              placeholder="2014"
              value={fromYear}
              onChange={(e) => setFromYear(e.target.value)}
            />
          </div>
          <div className="w-28">
            <label className="text-xs text-gray-500 block mb-1">
              To year <span className="text-gray-400">(blank = present)</span>
            </label>
            <Input
              type="number"
              placeholder="2022"
              value={toYear}
              onChange={(e) => setToYear(e.target.value)}
            />
          </div>
          <Button
            onClick={handleAdd}
            disabled={createMutation.isPending}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="h-4 w-4 mr-1" />
            {createMutation.isPending ? "Adding..." : "Add"}
          </Button>
        </div>
      </div>

      {/* Entries table */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            Career Entries{" "}
            <Badge className="ml-2 bg-gray-100 text-gray-700">
              {entries.length}
            </Badge>
          </h2>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">Order</TableHead>
              <TableHead>Club</TableHead>
              <TableHead className="w-32">From</TableHead>
              <TableHead className="w-32">To</TableHead>
              <TableHead className="text-right w-40">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-10">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </TableCell>
              </TableRow>
            ) : entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-10 text-gray-500">
                  No career entries yet — add the player's first club above.
                </TableCell>
              </TableRow>
            ) : (
              entries.map((entry) => {
                const isEditing = editingId === entry.id;
                return (
                  <TableRow key={entry.id}>
                    <TableCell className="text-gray-500">{entry.order}</TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Select value={editClubId} onValueChange={setEditClubId}>
                          <SelectTrigger className="w-64">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="max-h-80">
                            {clubs.map((c) => (
                              <SelectItem key={c.id} value={String(c.id)}>
                                {c.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="flex items-center gap-2">
                          {entry.club.logoUrl ? (
                            <img
                              src={entry.club.logoUrl}
                              alt={entry.club.name}
                              className="w-6 h-6 object-contain"
                            />
                          ) : null}
                          <span className="font-medium">{entry.club.name}</span>
                          {entry.club.league ? (
                            <Badge className="bg-gray-100 text-gray-600 text-xs">
                              {entry.club.league}
                            </Badge>
                          ) : null}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Input
                          type="number"
                          value={editFrom}
                          onChange={(e) => setEditFrom(e.target.value)}
                          className="w-24"
                        />
                      ) : (
                        entry.fromYear ?? "—"
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Input
                          type="number"
                          value={editTo}
                          onChange={(e) => setEditTo(e.target.value)}
                          placeholder="present"
                          className="w-24"
                        />
                      ) : entry.toYear === null ? (
                        <Badge className="bg-green-100 text-green-700">Present</Badge>
                      ) : (
                        entry.toYear
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {isEditing ? (
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => saveEdit(entry.id)}
                            disabled={updateMutation.isPending}
                            title="Save"
                          >
                            <Save className="h-4 w-4 text-green-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={cancelEdit}
                            title="Cancel"
                          >
                            <X className="h-4 w-4 text-gray-500" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => startEdit(entry)}
                            title="Edit"
                          >
                            <Pencil className="h-4 w-4 text-blue-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(entry.id)}
                            disabled={deleteMutation.isPending}
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </Layout>
  );
}
